import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';
import ComplianceFinanceWording from '@acorn-portal/shared/src/components/renewal/complianceFinanceWording';
import PaymentOptionsSelector, {
	IPaymentOptionValue,
} from '@acorn-portal/shared/src/components/renewal/paymentOptionsSelector';
import PageTitle from '@acorn-portal/shared/src/components/text/PageTitle';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { Form, Formik } from 'formik';
import paymentOptionsSchema from '@acorn-portal/shared/src/utils/validation/PaymentOptionsSchema';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { updatePaymentMethod } from '@acorn-portal/shared/src/store/renewal.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useEffect } from 'react';
import { bypassComplexRenewal } from '@acorn-portal/shared/src/utils';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export interface IPaymentOptionsValues {
	method?: string;
	deposit?: boolean;
}

const PaymentOptions = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const renewalState = useAppSelector((redux) => redux.renewal);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const initialValues: IPaymentOptionsValues = {
		method: undefined,
		deposit: undefined,
	};
	const bypassComplexRenewalFlag = bypassComplexRenewal(fullPolicy.product);

	const ncbDetails = renewalState.renewalRatingResponse?.coverDetails?.ncbDetails;
	const ncbYears = Number(ncbDetails?.ncbYears ?? 0);
	const ncdEntitlement = ncbDetails?.ncdEntitlement;
	const hasRenewalAddons =
		renewalState.renewalRatingResponse?.addOns && renewalState.renewalRatingResponse.addOns.length > 0;

	useEffect(() => {
		if (!fullPolicy.policyNumber) {
			navigate(ROUTE_PATHS.home);
		}
	}, []);

	const handleSubmit = async (values: IPaymentOptionsValues) => {
		dispatch(updatePaymentMethod(values));

		if (bypassComplexRenewalFlag) {
			navigate(ROUTE_PATHS.renewalCheckout);
		} else {
			const eligibleForExtras = (ncbYears > 0 && ncdEntitlement) || hasRenewalAddons;
			const path = eligibleForExtras ? ROUTE_PATHS.renewalOptionalExtras : ROUTE_PATHS.renewalCheckout;
			navigate(path);
		}
	};

	return (
		<FullPageLoadingSplash message='Loading Payment options...' loading={false}>
			<Paper
				elevation={1}
				sx={{
					borderRadius: '8px',
				}}
			>
				<Formik
					initialValues={initialValues}
					validationSchema={paymentOptionsSchema()}
					validateOnChange
					onSubmit={handleSubmit}
				>
					{(formik) => {
						return (
							<Form>
								<FormikLogErrors />
								<Box sx={{ margin: 2 }}>
									<PageTitle>Payment Options</PageTitle>
									<Box sx={{ display: 'flex', justifyContent: 'center' }}>
										<Typography
											sx={{
												textAlign: 'center',
												marginBottom: 4,
												maxWidth: { md: '70%' },
											}}
										>
											{fullPolicy.isShortTermPolicy
												? 'Please confirm the payment to proceed with renewing your policy'
												: 'Select your prefered payment method to continue your renewal'}
										</Typography>
									</Box>
									<Box sx={{}}>
										{renewalState.renewalRatingResponse && (
											<PaymentOptionsSelector
												paymentOptions={renewalState.renewalRatingResponse}
												value={formik.values}
												onChange={(values: IPaymentOptionValue) => {
													formik.setValues(values);
												}}
											/>
										)}
									</Box>
									{formik.values.method === 'finance' && (
										<ComplianceFinanceWording
											rate={renewalState.renewalRatingResponse!}
											deposit={formik.values.deposit}
										/>
									)}

									<Grid container columnSpacing={6} rowSpacing={2} paddingX={{ md: 6 }} sx={{ mt: 0, py: 3 }}>
										<Grid size={{ xs: 12, sm: 6 }} sx={{ display: { xs: 'none', md: 'flex' } }}>
											&nbsp;
										</Grid>
										<Grid size={{ xs: 12, sm: 6 }}>
											<Button
												variant='contained'
												fullWidth
												color='secondary'
												type='submit'
												endIcon={<ArrowForward />}
												disabled={!formik.values.method || !formik.isValid}
												size='large'
											>
												Continue
											</Button>
										</Grid>
									</Grid>
								</Box>
							</Form>
						);
					}}
				</Formik>
			</Paper>
		</FullPageLoadingSplash>
	);
};

export default PaymentOptions;
